import apiClient from "./apiClient";
import { getPatientById } from "./patientService";
import { getDoctorById } from "./doctorService";
import dayjs from "dayjs";

const REMINDER_API_URL = `/appointments/reminders`; // Use relative path since apiClient has baseURL

/** Prevent repeated POST /reminders/cancel/:id for the same appointment in one tab session. */
const reminderCancelAttemptedIds = new Set();
const reminderCancelInFlightIds = new Set();

const resolvePersonMobile = (person) =>
  person?.mobile ||
  person?.phone ||
  person?.mobileNumber ||
  person?.mobile_number ||
  person?.contactNumber ||
  person?.contact_number ||
  person?.contact ||
  null;

const resolvePersonName = (person) => {
  const combined = [person?.firstName, person?.lastName].filter(Boolean).join(" ").trim();
  return (
    person?.name ||
    person?.patientName ||
    person?.doctorName ||
    person?.fullName ||
    combined ||
    null
  );
};

const toReminderDateTime = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?/.test(value.trim())) {
    const trimmed = value.trim();
    return trimmed.length === 16 ? `${trimmed}:00` : trimmed.slice(0, 19);
  }
  const parsed = dayjs(value);
  if (!parsed.isValid()) return String(value);
  return parsed.format("YYYY-MM-DDTHH:mm:ss");
};

const buildReminderScheduleFlags = (appointmentDateTime) => {
  const parsed = dayjs(appointmentDateTime);
  if (!parsed.isValid()) {
    return {
      sendDayBeforeReminder: true,
      sendOnDayReminder: true,
    };
  }
  const daysUntil = parsed.startOf("day").diff(dayjs().startOf("day"), "day");
  return {
    sendDayBeforeReminder: daysUntil >= 1,
    sendOnDayReminder: daysUntil >= 0,
  };
};

/**
 * Schedules appointment reminder SMS for both patient and doctor
 * 
 * @param {Object} appointmentData - Appointment data
 * @param {number} appointmentData.appointmentId - Appointment ID
 * @param {string} appointmentData.appointmentDateTime - ISO datetime string
 * @param {boolean} appointmentData.isVirtual - Whether appointment is virtual
 * @param {string} [appointmentData.virtualLink] - Virtual meeting link (for virtual appointments)
 * @param {number} appointmentData.patientId - Patient ID
 * @param {string} appointmentData.patientMobile - Patient mobile number
 * @param {string} appointmentData.patientName - Patient name
 * @param {number} appointmentData.doctorId - Doctor ID
 * @param {string} appointmentData.doctorMobile - Doctor mobile number
 * @param {string} appointmentData.doctorName - Doctor name
 * @param {string} [appointmentData.departmentName] - Department name
 * 
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const scheduleAppointmentReminders = async (appointmentData) => {
  if (!appointmentData?.appointmentId || !appointmentData?.appointmentDateTime) {
    console.warn("[appointmentReminderService] Missing appointment ID or date/time; skip schedule");
    return {
      success: false,
      message: "Appointment ID and appointment date time are required",
    };
  }

  try {
    const appointmentDateTime = toReminderDateTime(appointmentData.appointmentDateTime);
    const { sendDayBeforeReminder, sendOnDayReminder } =
      buildReminderScheduleFlags(appointmentDateTime);
    const reminderTypes = [
      ...(sendDayBeforeReminder ? ["DAY_BEFORE"] : []),
      ...(sendOnDayReminder ? ["ON_DAY"] : []),
    ];
    const reminderOffsetsHours = [
      ...(sendDayBeforeReminder ? [24] : []),
      ...(sendOnDayReminder ? [0] : []),
    ];

    if (!sendDayBeforeReminder && !sendOnDayReminder) {
      return {
        success: true,
        message: "Appointment is in the past; no SMS reminders to schedule",
      };
    }

    // apiClient will automatically add organizationId via interceptor
    // This ensures the backend looks for the appointment in the correct organization-specific table
    const response = await apiClient.post(`${REMINDER_API_URL}/schedule`, {
      appointmentId: appointmentData.appointmentId,
      appointmentDateTime,
      appointmentDate: appointmentDateTime ? String(appointmentDateTime).slice(0, 10) : null,
      isVirtual: appointmentData.isVirtual || false,
      virtualLink: appointmentData.virtualLink || null,
      patientId: appointmentData.patientId,
      patientMobile: appointmentData.patientMobile,
      patientName: appointmentData.patientName,
      doctorId: appointmentData.doctorId,
      doctorMobile: appointmentData.doctorMobile,
      doctorName: appointmentData.doctorName,
      departmentName: appointmentData.departmentName || null,
      sendDayBeforeReminder,
      sendOnDayReminder,
      reminderTypes,
      reminderOffsetsHours,
    });

    reminderCancelAttemptedIds.delete(String(appointmentData.appointmentId));

    return {
      success: true,
      message: response.data?.message || "Appointment reminders scheduled successfully",
    };
  } catch (error) {
    console.error("[appointmentReminderService] Error scheduling appointment reminders:", error);
    console.error("[appointmentReminderService] Error response:", error.response?.data);
    console.error("[appointmentReminderService] Error status:", error.response?.status);
    
    // Provide more helpful error message
    const errorMessage = error?.response?.data?.message || 
                        error?.response?.data?.error || 
                        error?.response?.data ||
                        "Failed to schedule appointment reminders. Please try again.";
    
    console.error(`[appointmentReminderService] Backend error: ${errorMessage}`);
    
    // Don't throw - this is a non-critical operation
    // Reminder scheduling failure shouldn't break appointment creation
    return {
      success: false,
      message: errorMessage,
    };
  }
};

/**
 * Cancels scheduled reminders for an appointment (e.g., when appointment is cancelled or rescheduled)
 * 
 * @param {number} appointmentId - Appointment ID
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const cancelAppointmentReminders = async (appointmentId) => {
  if (!appointmentId) {
    throw new Error("Appointment ID is required");
  }

  const idKey = String(appointmentId);
  if (reminderCancelAttemptedIds.has(idKey) || reminderCancelInFlightIds.has(idKey)) {
    return {
      success: true,
      message: "Appointment reminders already cancelled for this session",
    };
  }

  reminderCancelInFlightIds.add(idKey);
  // Mark attempted immediately so parallel auto-cancel / remounts cannot spam the API.
  reminderCancelAttemptedIds.add(idKey);

  try {
    // apiClient will automatically add organizationId via interceptor
    const response = await apiClient.post(`${REMINDER_API_URL}/cancel/${appointmentId}`);
    return {
      success: true,
      message: response.data?.message || "Appointment reminders cancelled successfully",
    };
  } catch (error) {
    console.error("Error cancelling appointment reminders:", error);
    // Don't throw - this is a non-critical operation
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to cancel reminders",
    };
  } finally {
    reminderCancelInFlightIds.delete(idKey);
  }
};

/**
 * Reschedules reminders for an appointment (e.g., when appointment time is updated)
 * 
 * @param {Object} appointmentData - Updated appointment data
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const rescheduleAppointmentReminders = async (appointmentData) => {
  // First cancel existing reminders, then schedule new ones
  if (appointmentData.appointmentId) {
    await cancelAppointmentReminders(appointmentData.appointmentId);
  }
  return await scheduleAppointmentReminders(appointmentData);
};

/**
 * Helper function to schedule reminders after appointment creation
 * Fetches patient and doctor details and schedules reminders
 * 
 * @param {Object} appointment - Created appointment object
 * @param {number} appointment.id - Appointment ID
 * @param {string} appointment.appointmentDateTime - ISO datetime string
 * @param {boolean} [appointment.isVirtual] - Whether appointment is virtual
 * @param {string} [appointment.virtualLink] - Virtual meeting link
 * @param {number} appointment.patientId - Patient ID
 * @param {number} appointment.doctorId - Doctor ID
 * @param {Object} [options] - Additional options
 * @param {Object} [options.patient] - Pre-fetched patient data (optional)
 * @param {Object} [options.doctor] - Pre-fetched doctor data (optional)
 * 
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const scheduleRemindersForAppointment = async (appointment, options = {}) => {
  try {
    // Fetch patient and doctor details if not provided
    let patient = options.patient;
    let doctor = options.doctor;

    if (!patient && appointment.patientId) {
      try {
        patient = await getPatientById(appointment.patientId);
      } catch (error) {
        console.error("Error fetching patient details for reminder:", error);
        // Continue without patient details - backend can fetch them
      }
    }

    if (!doctor && appointment.doctorId) {
      try {
        doctor = await getDoctorById(appointment.doctorId);
      } catch (error) {
        console.error("Error fetching doctor details for reminder:", error);
        // Continue without doctor details - backend can fetch them
      }
    }

    // Prepare reminder data
    const reminderData = {
      appointmentId: appointment.id || appointment.appointmentId,
      appointmentDateTime:
        appointment.appointmentDateTime ||
        appointment.appointment_date_time ||
        appointment.appointmentDate ||
        appointment.date,
      isVirtual: appointment.isVirtual || appointment.is_virtual || false,
      virtualLink: appointment.virtualLink || appointment.virtual_link || appointment.virtualMeeting?.url || null,
      patientId: appointment.patientId || appointment.patient_id,
      patientMobile: resolvePersonMobile(patient) || resolvePersonMobile(appointment),
      patientName: resolvePersonName(patient) || resolvePersonName(appointment),
      doctorId: appointment.doctorId || appointment.doctor_id,
      doctorMobile: resolvePersonMobile(doctor),
      doctorName: resolvePersonName(doctor),
      departmentName: doctor?.departmentName || doctor?.department_name || appointment.departmentName || null,
    };

    // Cancel any previously scheduled reminders first so confirm/reschedule
    // does not send duplicate day-before / on-day SMS.
    if (reminderData.appointmentId) {
      await cancelAppointmentReminders(reminderData.appointmentId);
    }

    // Schedule reminders (non-blocking - don't fail if this fails)
    try {
      await scheduleAppointmentReminders(reminderData);
      console.log("Appointment reminders scheduled successfully");
    } catch (error) {
      // Log but don't throw - reminder scheduling failure shouldn't break appointment creation
      console.error("Failed to schedule appointment reminders:", error);
    }

    return { success: true };
  } catch (error) {
    // Don't throw - this is a non-critical operation
    console.error("Error in scheduleRemindersForAppointment:", error);
    return { success: false, message: error.message };
  }
};

